import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from '../../utils/formatCurrency';
import ElapsedTimer from '../ElapsedTimer';
import './TableCard.css';

const TableCard = React.memo(function TableCard({ table, orders, alerts, onClick }) {
  const { t } = useTranslation();

  const tableOrders = useMemo(() => {
    return orders.filter((o) => String(o.tableNumber) === String(table.tableNumber));
  }, [orders, table]);

  const hasUnpaidOrders = tableOrders.some((o) => o.status !== 'cancelled' && o.paymentStatus !== 'paid');
  const isOccupied = table.status === 'occupied' || !!table.currentSessionId || !!table.sessionId || hasUnpaidOrders;
  const readyCount = tableOrders.filter((o) => o.status === 'ready').length;
  const preparingCount = tableOrders.filter((o) => o.status === 'preparing').length;
  const pendingCount = tableOrders.filter((o) => o.status === 'pending').length;
  const inProgressCount = pendingCount + preparingCount;
  const totalAmount = tableOrders
    .filter((o) => o.status !== 'cancelled')
    .reduce((sum, o) => sum + (o.total || 0), 0);
  const unpaidAmount = tableOrders
    .filter((o) => o.status !== 'cancelled' && o.paymentStatus !== 'paid')
    .reduce((sum, o) => sum + (o.total || 0), 0);

  const hasBillRequest = alerts?.billRequest;
  const hasWaiterCall = alerts?.waiterCall;

  // First active order time for elapsed timer
  const firstOrderTime = useMemo(() => tableOrders
    .filter((o) => o.status !== 'delivered' && o.status !== 'cancelled')
    .map((o) => new Date(o.createdAt).getTime())
    .sort((a, b) => a - b)[0], [tableOrders]);

  // Determine status class
  const allPaid = isOccupied && tableOrders.length > 0 && unpaidAmount === 0;

  // Seats display
  const seatsDisplay = table.seats || table.capacity || '-';

  const renderContent = (elapsedMinutes) => {
    const getStatusClass = () => {
      if (!isOccupied) return 'available';
      if (allPaid) return 'all-paid';
      if (hasBillRequest) return 'bill-requested';
      if (readyCount > 0) return 'has-ready';
      if (elapsedMinutes > 15 && inProgressCount > 0) return 'waiting-long';
      if (inProgressCount > 0) return 'in-process';
      return 'occupied-idle';
    };

    const statusClass = getStatusClass();

    return (
      <button
        className={`table-card table-card--${statusClass}`}
        onClick={() => onClick(table)}
      >
        <div className="table-card-header">
          <div className="table-card-header-info">
            <span className="table-card-name">
              {t('orders.table')} {table.tableNumber}
            </span>
            {table.location && (
              <span className="table-card-location">{table.location}</span>
            )}
          </div>
          {(hasWaiterCall || hasBillRequest) && (
            <span className="table-card-alert-badge">
              {hasWaiterCall && '🔔'}
              {hasBillRequest && '🧾'}
            </span>
          )}
        </div>

        <div className="table-card-body">
          {!isOccupied ? (
            <div className="table-card-free">
              <span className="table-card-free-label">{t('waiter.free')}</span>
              {seatsDisplay !== '-' && (
                <span className="table-card-free-seats">
                  {t('waiter.seats', { count: seatsDisplay })}
                </span>
              )}
            </div>
          ) : (
            <>
              <div className="table-card-info">
                <span className="table-card-seats">{t('waiter.seats', { count: seatsDisplay })}</span>
                {elapsedMinutes > 0 && (
                  <>
                    <span className="table-card-separator">·</span>
                    <span className="table-card-time">{t('orders.minutesShort', { count: elapsedMinutes })}</span>
                  </>
                )}
              </div>
              <div className="table-card-status">
                {readyCount > 0 && (
                  <span className="table-card-badge badge-ready">
                    {readyCount} {t('waiter.ready')}
                  </span>
                )}
                {preparingCount > 0 && (
                  <span className="table-card-badge badge-preparing">
                    {preparingCount} {t('waiter.preparing')}
                  </span>
                )}
                {pendingCount > 0 && (
                  <span className="table-card-badge badge-pending">
                    {t('waiter.pending', { count: pendingCount })}
                  </span>
                )}
              </div>
              {totalAmount > 0 && (
                <div className="table-card-amount">
                  {unpaidAmount > 0
                    ? formatCurrency(unpaidAmount)
                    : formatCurrency(totalAmount)}
                  {allPaid && (
                    <span className="table-card-close-tag">{t('waiter.readyToClose', 'Cerrar')}</span>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </button>
    );
  };

  if (!firstOrderTime) return renderContent(0);

  return (
    <ElapsedTimer
      createdAt={new Date(firstOrderTime).toISOString()}
      render={renderContent}
    />
  );
});

export default TableCard;
